import {
  Typography,
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Skeleton,
  Paper
} from '@mui/material';
import { MapRankTexts, ResultModalToolTipRowTexts } from './MapRankTexts';
import { MapRankCss } from './MapRankCss';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import {
  commonTableHeaderIcon,
  KeywordOverviewResultCardsCss,
  KeywordOverviewTableCss
} from '../keywordResearch/keywordOverview/keywordOverviewCSS';
import { keywordResearchDashBoardCss } from '../keywordResearch/keywordResearchCSS';
import { CustomTooltip } from '../keywordResearch/CustomTooltip';
import React from 'react';
import HelpIcon from '@mui/icons-material/Help';
import { ResultModalRow } from './ResultModalRow';
import { ListDataTableCss } from '../keywordResearch/keywordsLists/KeywordListsCss';

export const ResultsModal = ({ handleClose, selectedData, reportData }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderDirection, setOrderDirection] = useState('asc');
  const [valueToOrderBy, setValueToOrderBy] = useState('');
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState(
    selectedData?.higher_ranked_competitors || []
  );

  useEffect(() => {
    setRows(selectedData?.higher_ranked_competitors || []);
  }, [selectedData, reportData]);

  const headers = ['#', 'Business Name', 'Reviews', 'Rating'];

  const columnToDataKeyMap: any = {
    'Business Name': 'title',
    Reviews: 'num_reviews',
    Rating: 'review_rating'
  };

  const handleRequestSort = (property) => {
    const isAsc = valueToOrderBy === property && orderDirection === 'asc';
    setValueToOrderBy(property);
    setOrderDirection(isAsc ? 'desc' : 'asc');
  };

  const sortedData = [...rows]?.sort((a, b) => {
    const aValue = a[valueToOrderBy] ?? '';
    const bValue = b[valueToOrderBy] ?? '';

    if (orderDirection === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    }
    return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to the first page
  };
  return (
    <>
      <Box sx={MapRankCss?.resultModalBoxSx}>
        <Box sx={MapRankCss?.resultModalTitleBoxSx}>
          <Typography sx={MapRankCss?.resultModalTitleSx}>
            {MapRankTexts?.resultModalTitle}
            {`“${reportData?.keyword}”`}
          </Typography>
          <IconButton
            onClick={() => {
              handleClose();
            }}
            disableRipple
            aria-label="close"
          >
            <CloseIcon sx={MapRankCss?.closeIcon} />
          </IconButton>
        </Box>
        <Typography sx={MapRankCss?.resultModalDescSx}>
          {`Search Location - Latitude: ${selectedData?.latitude}, Longitude: ${selectedData?.longitude} (Your Current Business location)`}
        </Typography>
        <Box sx={MapRankCss?.resultModalTableBox}>
          <TableContainer
            sx={{
              maxHeight: '340px',
              ...KeywordOverviewTableCss?.tableContainer,
              '&::-webkit-scrollbar': {
                width: '4px' // Thinner scrollbar
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'var(--color-border-light2)',
                borderRadius: '8px'
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent'
              }
            }}
            component={Paper}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={KeywordOverviewTableCss?.tableHeaderRow}>
                  {headers?.map((headCell, index) => {
                    const dataKey = columnToDataKeyMap[headCell] || headCell;

                    return (
                      <TableCell key={headCell} sx={{ minWidth: 50 }}>
                        {headCell !== '#' ? (
                          <TableSortLabel
                            sx={KeywordOverviewTableCss?.tableSortLabel}
                            active={valueToOrderBy === dataKey}
                            direction={
                              valueToOrderBy === dataKey
                                ? (orderDirection as 'asc' | 'desc')
                                : 'asc'
                            }
                            onClick={() => handleRequestSort(dataKey)}
                          >
                            <Typography
                              sx={KeywordOverviewTableCss?.tableHeadText}
                            >
                              {index > 0 && (
                                <CustomTooltip
                                  title={
                                    <Box>
                                      <Typography
                                        sx={
                                          keywordResearchDashBoardCss?.customTollTipText
                                        }
                                      >
                                        {ResultModalToolTipRowTexts[index]}
                                      </Typography>
                                    </Box>
                                  }
                                  placement="bottom"
                                  arrow
                                  PopperProps={{
                                    style: { zIndex: 1600 }
                                  }}
                                >
                                  <HelpIcon
                                    sx={{
                                      mr: 1,
                                      ...commonTableHeaderIcon?.helpIcon,
                                      ...KeywordOverviewResultCardsCss?.helpIconHead
                                    }}
                                    fontSize="small"
                                  />
                                </CustomTooltip>
                              )}
                              {headCell}
                            </Typography>
                          </TableSortLabel>
                        ) : (
                          <Typography
                            sx={KeywordOverviewTableCss?.tableHeadText}
                          >
                            {headCell}
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {loader ? (
                  Array.from({
                    length: Math.max(rows?.length || 3, 3)
                  }).map((_, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {Array.from({ length: 4 }).map((_, cellIndex) => (
                        <TableCell key={cellIndex}>
                          <Skeleton
                            variant="rectangular"
                            animation="wave"
                            width={
                              cellIndex === 0
                                ? 120
                                : cellIndex === 1
                                ? 200
                                : cellIndex === 2
                                ? 150
                                : '80%' // Adjust widths accordingly
                            }
                            height={20}
                            sx={{
                              borderRadius: '100px',
                              marginTop: '8px',
                              marginBottom: '8px',
                              padding: '5px'
                            }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : sortedData?.length == 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      align="center"
                      sx={{ paddingLeft: '5px !important' }}
                    >
                      {' '}
                      <Typography>{MapRankTexts?.noDataFound}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  (rowsPerPage > 0
                    ? sortedData?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                    : sortedData
                  )?.map((row, index) => {
                    return (
                      <React.Fragment key={index}>
                        <ResultModalRow key={index} row={row} index={index} />
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {sortedData && sortedData?.length > 0 && (
            <TablePagination
              sx={ListDataTableCss?.pagination}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Box>
        <Button
          variant="contained"
          fullWidth
          sx={MapRankCss?.resultModalBtnSx}
          onClick={() => {
            handleClose();
          }}
        >
          {MapRankTexts?.resultModalBtnText}
        </Button>
      </Box>
    </>
  );
};
